import Combobox from "@/components/core/forms/combobox";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import clientService from "@/features/dashboard/api/services/client-service";
import { type Project, projectSchema } from "@/features/dashboard/types/project";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

// Utility function to safely extract date part without timezone conversion
const extractDateOnly = (dateString: string | null | undefined): string | null => {
	if (!dateString || typeof dateString !== "string") return null;
	return dateString.substring(0, 10);
};

interface ProjectFormProps {
	project?: Project;
	onSubmit: (values: Project) => Promise<void>;
	isSubmitting?: boolean;
}

const statusOptions = [
	{ value: "active", label: "Active" },
	{ value: "inactive", label: "Inactive" },
	{ value: "completed", label: "Completed" },
];

const categoryOptions = [
	{ value: "one-time", label: "One-time Project" },
	{ value: "recurring", label: "Recurring Project" },
	{ value: "recurring-one-time", label: "Recurring & One-time Project" },
];

export function ProjectForm({ project, onSubmit }: ProjectFormProps) {
	const form = useForm<Project>({
		resolver: zodResolver(projectSchema),
		defaultValues: project
			? {
					...project,
					date_start: extractDateOnly(project.date_start),
					noa_date: extractDateOnly(project.noa_date),
					ntp_date: extractDateOnly(project.ntp_date),
					contract_date: extractDateOnly(project.contract_date),
					purchase_order: extractDateOnly(project.purchase_order),
				}
			: {
					name: "",
					description: "",
					client_id: null,
					date_start: "",
					status: "active",
					category: "one-time",
					contact_person: "",
					contact_number: "",
					noa_date: "",
					ntp_date: "",
					contract_date: "",
					purchase_order: "",
					notes: "",
				},
	});

	return (
		<Form {...form}>
			<form id="project-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-6">
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Project Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter project name" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Description</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter project description" rows={3} {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="client_id"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Client</FormLabel>
										<FormGroup>
											<Combobox
												value={field.value ? field.value.toString() : ""}
												onValueChange={(value) => field.onChange(value || null)}
												loadOptions={async (value: string) => {
													const response = await clientService.search(value);
													return response.data.map((client) => ({
														label: client.display_name,
														value: client.id.toString(),
													}));
												}}
												placeholder="Select a client..."
												placeholderValue={
													project?.client
														? {
																value: project.client.id.toString(),
																label: project.client.display_name || project.client.company || project.client.name,
															}
														: undefined
												}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="status"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Status</FormLabel>
										<FormGroup>
											<Select placeholder="Select status" options={statusOptions} defaultValue={field.value} onValueChange={field.onChange} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="category"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Category</FormLabel>
										<FormGroup>
											<Select placeholder="Select category" options={categoryOptions} defaultValue={field.value} onValueChange={field.onChange} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="date_start"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Start Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Contact Information */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="contact_person"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Contact Person</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter contact person name" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="contact_number"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Contact Number</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter contact number" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Important Dates */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="noa_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel>NOA Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="ntp_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel>NTP Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="contract_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Contract Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="purchase_order"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Purchase Order</FormLabel>
										<FormGroup>
											<Input className="input" type="date" placeholder="Enter purchase order number" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Notes */}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="notes"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Notes</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter additional notes" rows={3} {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
